/**
 * What the Suburbs tool says after a save, an undo or a failure, as a toast (#273; Ivan 2026-11-02: no status line).
 * Errors stay until closed; anything else shows briefly, each one replacing the last, so painting suburb after suburb
 * doesn't stack them. A map edit's save carries an Undo button that does what Cmd+Z does (the same undo stack), naming
 * what it takes back (its suburbs, for up to three: undo-label.ts); that toast stays long enough to reach it. Progress lines ("Loading NSW…") and a state's suburb
 * count aren't toasts: the left menu shows the counts. Pure, tested in status-toast.spec.ts.
 */

/** Two toast slots: errors stack and stay; the rest replace each other. */
export const TOAST_KEY = 'suburbs';
export const ERROR_TOAST_KEY = 'suburbs-errors';

export interface StatusToast {
  key: typeof TOAST_KEY | typeof ERROR_TOAST_KEY;
  severity: 'success' | 'info' | 'error';
  detail: string;
  /** Stays until closed. */
  sticky: boolean;
  /** How long it shows, in ms (0 when sticky). */
  life: number;
  /** A map edit's save: what its Undo button takes back (the undo stack's label for it). */
  undo?: string;
}

/** Long enough to read a save and reach its Undo button. */
export const UNDO_LIFE = 8000;

const PROGRESS = /(…|\.\.\.)$/;
const STATE_LOADED = /^[\d,]+ suburbs in [A-Z]+\.$/;
const DONE = /^(Saved|Undid|Removed|Deleted|Converted|Filled|Grew|Added|Checks done)\b/;

/** `undo`: the message is a map edit's save, and this is what its Undo button takes back. */
export function statusToast(
  message: string,
  error = false,
  undo: string | null = null
): StatusToast | null {
  const detail = (message ?? '').trim();
  if (!detail) return null;
  if (error) return { key: ERROR_TOAST_KEY, severity: 'error', detail, sticky: true, life: 0 };
  if (PROGRESS.test(detail) || STATE_LOADED.test(detail)) return null;
  const done = DONE.test(detail);
  const label = undo?.trim();
  return {
    key: TOAST_KEY,
    severity: done ? 'success' : 'info',
    detail,
    sticky: false,
    life: label ? UNDO_LIFE : done ? 2500 : 4000,
    ...(label ? { undo: label } : {}),
  };
}
